/**
 * Design System Theme
 *
 * Complete theme object for use with styled-components ThemeProvider.
 *
 * Usage with ThemeProvider:
 * import { ThemeProvider } from 'styled-components';
 * import { theme } from 'admin/theme';
 *
 * <ThemeProvider theme={theme}>
 *   <YourComponent />
 * </ThemeProvider>
 *
 * Usage in styled-components:
 * const Button = styled.button`
 *   background: ${props => props.theme.colors.green['500']};
 *   color: ${props => props.theme.colors.red['200']};
 *   padding: ${props => props.theme.spacing['4']};
 * `;
 *
 * Or import directly without ThemeProvider:
 * import { colors } from 'admin/theme';
 *
 * const Button = styled.button`
 *   background: ${colors.green['500']};
 * `;
 */

// Export to use with styled components and ThemeProvider
import { borderRadius } from './tokens/borderRadius';
import { colors, semanticColors } from './tokens/colors';
import { shadows } from './tokens/shadows';
import { spacing } from './tokens/spacing';
import { typography } from './tokens/typography';

export const theme = {
  colors,
  semanticColors,
  spacing,
  borderRadius,
  shadows,
  typography,
} as const;

export type Theme = typeof theme;

// Export to use as a direct import without ThemeProvider
export type { BorderRadius } from './tokens/borderRadius';
export type { Colors, SemanticColors } from './tokens/colors';
export type { Shadows } from './tokens/shadows';
export type { Spacing } from './tokens/spacing';
export type { Typography } from './tokens/typography';
export { pxToRem, remToPx } from './utils';
export { borderRadius, colors, semanticColors, shadows, spacing, typography };
